import { computed, onMounted, reactive, ref, watch } from "vue";
import parceriaService from "@/service/parceriaService";
import userInfos from "@/utils/userInfos";
import { STATUS_PARCERIA } from "@/constants/parcerias";

const TAMANHO_PAGINA_PADRAO = 8;

function extrairLista(dados) {
  if (!dados) return [];
  if (Array.isArray(dados)) return dados;
  if (Array.isArray(dados.content)) return dados.content;
  if (Array.isArray(dados.solicitacoes)) return dados.solicitacoes;
  if (Array.isArray(dados.parcerias)) return dados.parcerias;
  if (Array.isArray(dados.solicitacoesParceria)) return dados.solicitacoesParceria;
  if (Array.isArray(dados.cards)) return dados.cards;
  if (Array.isArray(dados.items)) return dados.items;
  if (Array.isArray(dados.itens)) return dados.itens;
  if (Array.isArray(dados.dados)) return dados.dados;
  if (Array.isArray(dados.data)) return dados.data;
  if (Array.isArray(dados.elementos)) return dados.elementos;
  if (Array.isArray(dados.elements)) return dados.elements;

  if (typeof dados === "object" && dados !== null) {
    const chavesArray = Object.keys(dados).filter((k) => Array.isArray(dados[k]));
    if (chavesArray.length === 1) {
      return dados[chavesArray[0]];
    }
  }
  return [];
}

function obterNomeStatus(solicitacao) {
  if (!solicitacao) return "";
  const status =
    solicitacao.status ??
    solicitacao.statusParceria ??
    solicitacao.statusSolicitacao;
  if (typeof status === "string") return status;
  if (typeof status === "object" && status !== null) {
    return status.nome || status.status || status.descricao || status.valor || "";
  }
  return String(status || "");
}

function normalizarSolicitacao(p) {
  if (!p) return p;
  const s = String(obterNomeStatus(p)).trim().toUpperCase();
  const statusNormalizado = STATUS_PARCERIA[s] || s;
  return {
    ...p,
    nomeAcao: p.nomeAcao || p.tituloAcao || p.titulo || "Parceria sem título",
    nomeCoordenador: p.nomeCoordenador || p.solicitante?.nome || "Coordenador não informado",
    dataSolicitacao: p.dataSolicitacao || p.dataCriacao || p.criadoEm || null,
    status: statusNormalizado,
  };
}

/**
 * Composable exclusivo para o Dashboard de Análise de Parcerias da PREX (Quem Recebe).
 * Gerencia a listagem global, triagem, analistas e decisões institucionais da PREX.
 */
export function useParceriasPrex({ tamanhoPagina = TAMANHO_PAGINA_PADRAO, autoCarregar = true } = {}) {
  // ─── Estado principal ────────────────────────────────────────────────────
  const todasParcerias = ref([]);
  const filtros = reactive({ busca: "", status: "" });
  const paginaAtual = ref(0);
  const carregando = ref(false);
  const erro = ref("");
  const avaliacoesPorParceria = ref({});

  // ─── Dados filtrados e ordenados (EM_ANALISE no topo, mais recentes primeiro) ─
  const parceriasFiltradas = computed(() => {
    let lista = [...todasParcerias.value];

    if (filtros.status) {
      lista = lista.filter((p) => p.status === filtros.status);
    }

    if (filtros.busca.trim()) {
      const termo = filtros.busca.trim().toLowerCase();
      lista = lista.filter(
        (p) =>
          (p.titulo || "").toLowerCase().includes(termo) ||
          (p.nomeAcao || p.instituicaoParceira || "")
            .toLowerCase()
            .includes(termo) ||
          (p.nomeCoordenador || p.solicitante?.nome || "")
            .toLowerCase()
            .includes(termo)
      );
    }

    // Ordenação estritamente cronológica decrescente: as solicitações mais recentes sempre aparecem no topo
    lista.sort((a, b) => {
      const dataA = a.dataSolicitacao
        ? new Date(a.dataSolicitacao).getTime()
        : 0;
      const dataB = b.dataSolicitacao
        ? new Date(b.dataSolicitacao).getTime()
        : 0;
      return dataB - dataA;
    });

    return lista;
  });

  // ─── Resumo computado para a PREX (Quem Recebe) ──────────────────────────
  const resumo = computed(() => {
    const lista = todasParcerias.value;

    return {
      total: lista.length,
      pendentes: lista.filter(
        (p) => p.status === STATUS_PARCERIA.FORMULARIO_RECEBIDO
      ).length,
      emAnalise: lista.filter(
        (p) => p.status === STATUS_PARCERIA.EM_ANALISE
      ).length,
      emArticulacao: lista.filter(
        (p) => p.status === STATUS_PARCERIA.EM_ARTICULACAO_E_FORMALIZACAO
      ).length,
      aguardandoReitoria: lista.filter(
        (p) => p.status === STATUS_PARCERIA.AGUARDANDO_APROVACAO_REITORIA
      ).length,
      aprovadas: lista.filter(
        (p) => p.status === STATUS_PARCERIA.PARCERIA_FORMALIZADA
      ).length,
      formalizadas: lista.filter(
        (p) => p.status === STATUS_PARCERIA.PARCERIA_FORMALIZADA
      ).length,
      rejeitadas: lista.filter(
        (p) => p.status === STATUS_PARCERIA.REJEITADA
      ).length,
      canceladas: lista.filter(
        (p) => p.status === STATUS_PARCERIA.CANCELADA
      ).length,
    };
  });

  // ─── Paginação computada ─────────────────────────────────────────────────
  const totalElementos = computed(() => parceriasFiltradas.value.length);
  const totalPaginas = computed(() =>
    Math.max(Math.ceil(totalElementos.value / tamanhoPagina), 1)
  );

  const inicioPagina = computed(() =>
    totalElementos.value ? paginaAtual.value * tamanhoPagina + 1 : 0
  );

  const fimPagina = computed(() =>
    Math.min((paginaAtual.value + 1) * tamanhoPagina, totalElementos.value)
  );

  const parcerias = computed(() => {
    const inicio = paginaAtual.value * tamanhoPagina;
    const fim = inicio + tamanhoPagina;
    return parceriasFiltradas.value.slice(inicio, fim);
  });

  // ─── Helpers ─────────────────────────────────────────────────────────────
  function extrairMensagemErro(error, padrao = "Ocorreu um erro inesperado.") {
    const dados = error?.response?.data || error?.data;
    const mensagem = dados?.message || error?.message || padrao;
    const errosPorCampo = Array.isArray(dados?.errors)
      ? dados.errors
          .map(({ field, message }) =>
            [field, message].filter(Boolean).join(": ")
          )
          .filter(Boolean)
          .join(" • ")
      : "";
    return errosPorCampo ? `${mensagem} — ${errosPorCampo}` : mensagem;
  }

  // ─── Carregamento de dados PREX ──────────────────────────────────────────
  async function carregarDados() {
    carregando.value = true;
    erro.value = "";
    try {
      const usuarioLogado = userInfos.getUserInfos();
      console.log("[PREX Dashboard] Usuário logado no token:", usuarioLogado);
      console.log("[PREX Dashboard] Scope do usuário:", usuarioLogado?.scope);

      console.log("[PREX Dashboard] Solicitando /api/solicitacoes-parceria...");
      let resultado;
      try {
        resultado = await parceriaService.listarParaAnalise({
          page: 0,
          size: 2000,
        });
      } catch (errPaginacao) {
        console.warn(
          "[PREX Dashboard] Erro ao buscar com paginação, tentando sem parâmetros:",
          errPaginacao
        );
        resultado = await parceriaService.listarParaAnalise({});
      }

      console.log("[PREX Dashboard] Resposta bruta da API:", resultado);

      const dados = resultado?.data ?? resultado;
      const listaBruta = extrairLista(dados);

      console.log(
        `[PREX Dashboard] Total de parcerias retornadas pelo backend: ${listaBruta.length}`,
        listaBruta
      );

      // Normaliza status de entrada (PENDENTE, EM_EDICAO, etc.) para FORMULARIO_RECEBIDO ("Aguardando análise")
      todasParcerias.value = listaBruta.map(normalizarSolicitacao);

      if (paginaAtual.value >= totalPaginas.value) {
        paginaAtual.value = Math.max(totalPaginas.value - 1, 0);
      }
    } catch (err) {
      console.error("[PREX Dashboard] Erro fatal ao carregar solicitações:", err);
      erro.value = extrairMensagemErro(
        err,
        "Não foi possível carregar as solicitações."
      );
    } finally {
      carregando.value = false;
    }
  }

  // ─── Filtros e paginação ─────────────────────────────────────────────────
  function aplicarFiltros() {
    paginaAtual.value = 0;
  }

  function selecionarStatus(status) {
    filtros.status = status;
    aplicarFiltros();
  }

  function mudarPagina(pagina) {
    if (pagina < 0 || pagina >= totalPaginas.value) return;
    paginaAtual.value = pagina;
  }

  // ─── Avaliações / Histórico do Analista ───────────────────────────────────
  async function carregarAvaliacoesVisiveis(lista) {
    if (!lista || lista.length === 0) return;
    const promessas = lista.map(async (p) => {
      if (!avaliacoesPorParceria.value[p.id]) {
        try {
          const avaliacoes =
            await parceriaService.listarAvaliacoesDaSolicitacao(p.id);
          avaliacoes.sort(
            (a, b) => new Date(a.dataAvaliacao) - new Date(b.dataAvaliacao)
          );
          avaliacoesPorParceria.value[p.id] = avaliacoes;
        } catch (e) {
          avaliacoesPorParceria.value[p.id] = [];
        }
      }
    });
    await Promise.all(promessas);
  }

  watch(
    parcerias,
    (novaLista) => {
      carregarAvaliacoesVisiveis(novaLista);
    },
    { immediate: true }
  );

  // ─── Ações PREX ──────────────────────────────────────────────────────────
  async function aprovarParceria(id, observacao = "") {
    await parceriaService.alterarStatus(
      id,
      STATUS_PARCERIA.PARCERIA_FORMALIZADA,
      observacao || "Parceria aprovada e formalizada"
    );
    delete avaliacoesPorParceria.value[id];
    await carregarDados();
  }

  async function reprovarParceria(id, motivo) {
    await parceriaService.rejeitar(id, motivo);
    delete avaliacoesPorParceria.value[id];
    await carregarDados();
  }

  async function marcarComoEmAnalise(id) {
    await parceriaService.alterarStatus(
      id,
      STATUS_PARCERIA.EM_ANALISE,
      "Análise iniciada"
    );
    delete avaliacoesPorParceria.value[id];
    await carregarDados();
  }

  async function avancarParaArticulacao(id, observacao = "") {
    await parceriaService.alterarStatus(
      id,
      STATUS_PARCERIA.EM_ARTICULACAO_E_FORMALIZACAO,
      observacao || "Avançado para articulação e formalização"
    );
    delete avaliacoesPorParceria.value[id];
    await carregarDados();
  }

  async function avancarParaReitoria(id, observacao = "") {
    await parceriaService.alterarStatus(
      id,
      STATUS_PARCERIA.AGUARDANDO_APROVACAO_REITORIA,
      observacao || "Encaminhado para aprovação da reitoria"
    );
    delete avaliacoesPorParceria.value[id];
    await carregarDados();
  }

  async function cancelarParceria(id, motivo = "") {
    await parceriaService.alterarStatus(
      id,
      STATUS_PARCERIA.CANCELADA,
      motivo || "Parceria cancelada após formalização"
    );
    delete avaliacoesPorParceria.value[id];
    await carregarDados();
  }

  if (autoCarregar) {
    onMounted(carregarDados);
  }

  return {
    parcerias,
    resumo,
    filtros,
    paginaAtual,
    totalPaginas,
    totalElementos,
    carregando,
    erro,
    avaliacoesPorParceria,
    inicioPagina,
    fimPagina,
    carregarDados,
    aplicarFiltros,
    selecionarStatus,
    mudarPagina,
    aprovarParceria,
    reprovarParceria,
    marcarComoEmAnalise,
    avancarParaArticulacao,
    avancarParaReitoria,
    cancelarParceria,
    extrairMensagemErro,
    STATUS_PARCERIA,
  };
}
